// src/app/features/payments/payments.component.ts
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormsModule,
} from '@angular/forms';
import { MaterialModule } from '../../shared/material.module';
import { BreadcrumbComponent } from 'src/app/components/shared/breadcrumb/breadcrumb.component';
import {
  PaymentMethod,
  PaymentService,
  CreatePaymentMethodRequest,
  UpdatePaymentMethodRequest,
} from 'src/app/services/payment.service';
import { AuthService } from 'src/app/services/auth.service';
import { CardsService } from 'src/app/services/cards.service';

type ViewMode = 'grid' | 'list';

@Component({
  selector: 'app-payments',
  templateUrl: './payments.component.html',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, MaterialModule, FormsModule, BreadcrumbComponent],
  styleUrls: ['./payments.component.scss'],
})
export class PaymentsComponent implements OnInit {
  paymentMethods: PaymentMethod[] = [];
  loading = false;
  creating = false;
  error = '';
  editingMethod: PaymentMethod | null = null;

  // The current card ID
  get currentCardId(): string | null {
    return this.cardsService.selectedCard()?.id ?? null;
  }

  // UI state
  viewMode: ViewMode = 'grid';
  showCreateForm = false;
  showPaymentDialog = false;
  testPaymentAmount = 299;

  paymentForm: FormGroup;
  qrPreview: string | null = null;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private paymentService: PaymentService,
    private cardsService: CardsService
  ) {
    // Note: bank fields are not marked required here because we allow external-only
    // payment methods. We'll enforce that if the user fills any bank field then
    // all bank fields are mandatory at submit time.
    this.paymentForm = this.fb.group({
      bankName: [''],
      accountHolder: [''],
      accountNumber: [''],
      ifsc: [''],
      upiId: [''],
      qrCodeUrl: [''],
      paymentLink: [''],
    });
  }

  ngOnInit() {
    if (this.authService.currentUser && this.authService.idToken) {
      this.fetchPaymentMethods();
    } else {
      // Wait for token to be available
      const interval = setInterval(() => {
        if (this.authService.currentUser && this.authService.idToken) {
          clearInterval(interval);
          this.fetchPaymentMethods();
        }
      }, 100);
    }
  }

  /** Fetch payment methods for the current card */
  fetchPaymentMethods(): void {
    const cardId = this.currentCardId;
    if (!cardId) {
      this.error = 'No card selected';
      return;
    }
    this.loading = true;
    this.error = '';
    this.paymentService.fetchPaymentDetails(cardId).subscribe({
      next: (methods) => {
        this.paymentMethods = methods || [];
        // If there is already a payment method, make sure Add becomes Edit
        if (this.paymentMethods.length > 0) {
          // Preselect first method as the single stored method
          this.editingMethod = this.paymentMethods[0];
        } else {
          this.editingMethod = null;
        }
        this.loading = false;
      },
      error: () => {
        this.error = 'Failed to fetch payment methods';
        this.loading = false;
      },
    });
  }

  /** Open form in create or edit mode
   * If a payment method already exists, default to edit the existing one.
   */
  openCreateForm(method?: PaymentMethod): void {
    this.showCreateForm = true;
    this.error = '';

    // If method provided explicitly, edit it
    if (method) {
      this.editingMethod = method;
    } else if (this.paymentMethods.length > 0) {
      // If a method already exists, open edit mode for the single method
      this.editingMethod = this.paymentMethods[0];
    } else {
      this.editingMethod = null;
    }

    if (this.editingMethod) {
      this.paymentForm.patchValue({
        bankName: this.editingMethod.bankName ?? '',
        accountHolder: this.editingMethod.accountHolder ?? '',
        accountNumber: this.editingMethod.accountNumber ?? '',
        ifsc: this.editingMethod.ifsc ?? '',
        upiId: this.editingMethod.upiId || '',
        qrCodeUrl: this.editingMethod.qrCodeUrl || '',
        paymentLink: this.editingMethod.paymentLink || ''
      });
      this.qrPreview = this.editingMethod.qrCodeUrl || null;
    } else {
      this.resetForm();
    }
  }

  /** Close the form and reset */
  closeCreateForm(): void {
    this.showCreateForm = false;
    this.resetForm();
  }

  /** Handle create/update submit */
  onSubmit(): void {
    const cardId = this.currentCardId;
    if (!cardId) {
      this.error = 'No card selected';
      return;
    }
    // Custom validation: if any bank field is provided, require all bank fields.
    if (!this.canSave()) {
      this.error = 'Provide all bank details (Bank Name, Account Holder, Account Number, IFSC) or provide at least one external method (UPI, Payment Link, or QR).';
      this.creating = false;
      return;
    }

    this.creating = true;
    this.error = '';
    const formValue = this.paymentForm.value;

    // Build payload - allow partial updates and multiple types
    const payload: CreatePaymentMethodRequest | UpdatePaymentMethodRequest = {
      bankName: formValue.bankName?.trim(),
      accountHolder: formValue.accountHolder?.trim(),
      accountNumber: formValue.accountNumber?.trim(),
      ifsc: formValue.ifsc?.trim(),
      upiId: formValue.upiId?.trim() || undefined,
      qrCodeUrl: formValue.qrCodeUrl?.trim() || this.qrPreview || undefined,
      paymentLink: formValue.paymentLink?.trim() || undefined
    };

    // If an existing method is present -> UPDATE
    if (this.editingMethod || this.paymentMethods.length > 0) {
      this.paymentService.updatePaymentDetails(cardId, payload).subscribe({
        next: () => {
          this.closeCreateForm();
          this.fetchPaymentMethods();
          this.creating = false;
        },
        error: () => {
          this.error = 'Failed to update payment method';
          this.creating = false;
        },
      });
    } else {
      // CREATE
      this.paymentService.createPaymentDetails(cardId, payload as CreatePaymentMethodRequest).subscribe({
        next: () => {
          this.closeCreateForm();
          this.fetchPaymentMethods();
          this.creating = false;
        },
        error: () => {
          this.error = 'Failed to add payment method';
          this.creating = false;
        },
      });
    }
  }

  /** Determine if the form can be saved according to custom rules:
   * - If any bank field is filled (non-empty after trim) then all bank fields must be provided.
   * - Otherwise at least one external method (upiId, paymentLink, qrPreview) must be present.
   */
  canSave(): boolean {
    const v = this.paymentForm.value;
    const bankName = v.bankName?.toString().trim();
    const accountHolder = v.accountHolder?.toString().trim();
    const accountNumber = v.accountNumber?.toString().trim();
    const ifsc = v.ifsc?.toString().trim();

    const anyBankProvided = !!(bankName || accountHolder || accountNumber || ifsc);
    if (anyBankProvided) {
      return !!(bankName && accountHolder && accountNumber && ifsc);
    }

    const hasExternal = !!((v.upiId && v.upiId.toString().trim()) || (v.paymentLink && v.paymentLink.toString().trim()) || this.qrPreview);
    return hasExternal;
  }

  /** File input handler for QR code image */
  onQrFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      this.qrPreview = reader.result as string;
      this.paymentForm.patchValue({ qrCodeUrl: this.qrPreview });
    };
    reader.readAsDataURL(file);
  }

  removeQrImage(): void {
    this.qrPreview = null;
    this.paymentForm.patchValue({ qrCodeUrl: '' });
  }

  /** Delete a payment method (only cardId required) */
  deletePaymentMethod(): void {
    if (!confirm('Are you sure you want to delete this payment method?')) {
      return;
    }

    const cardId = this.currentCardId;
    if (!cardId) {
      this.error = 'No card selected';
      return;
    }

    this.loading = true;
    this.error = '';
    this.paymentService.deletePaymentDetails(cardId).subscribe({
      next: () => {
        this.fetchPaymentMethods();
        this.loading = false;
      },
      error: () => {
        this.error = 'Failed to delete payment method';
        this.loading = false;
      },
    });
  }

  /** View payment method details */
  viewMethod(method: PaymentMethod): void {
    // Implement view logic or navigate to detail page
    console.log('Viewing method:', method);
  }

  /** Initiate test payment */
  initiatePayment(): void {
    this.showPaymentDialog = true;
  }

  /** Close payment dialog */
  closePaymentDialog(): void {
    this.showPaymentDialog = false;
    this.testPaymentAmount = 299;
  }

  /** Process test payment */
  processTestPayment(): void {
    const cardId = this.currentCardId;
    if (!cardId || !this.testPaymentAmount) return;

    this.paymentService.initiatePayment(cardId, this.testPaymentAmount).subscribe({
      next: (result) => {
        console.log('Payment initiated:', result);
        this.closePaymentDialog();
        // Here you would typically redirect to payment gateway
        alert(`Payment initiated with Order ID: ${result.orderId}`);
      },
      error: () => {
        this.error = 'Failed to initiate payment';
      },
    });
  }

  /** Reset form controls */
  resetForm(): void {
    this.editingMethod = null;
    this.qrPreview = null;
    this.paymentForm.reset({
      bankName: '',
      accountHolder: '',
      accountNumber: '',
      ifsc: '',
      upiId: '',
      qrCodeUrl: '',
      paymentLink: ''
    });
  }

  /** Clear error message */
  clearError(): void {
    this.error = '';
  }

  /** Get last 4 digits of account number */
  getLastFourDigits(accountNumber: string): string {
    return accountNumber ? accountNumber.slice(-4) : '';
  }

  /** Format ISO date string to local date */
  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'N/A';
    }
  }

  /** Open external payment link in a new tab */
  openExternalPaymentLink(url?: string): void {
    if (!url) return;
    const normalized = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
    window.open(normalized, '_blank');
  }

  /** Determine if the current form is only for external payment (link / upi / qr) */
  hasExternalPaymentOnly(): boolean {
    const v = this.paymentForm.value;
    const hasBank = (v.bankName && v.accountHolder && v.accountNumber && v.ifsc);
    const hasExternal = (v.upiId && v.upiId.trim()) || (v.paymentLink && v.paymentLink.trim()) || (v.qrCodeUrl && v.qrCodeUrl.trim());
    return !hasBank && !!hasExternal;
  }

  /** Return the domain part of a URL string safely, or null */
  getDomain(url?: string | null): string | null {
    if (!url) return null;
    try {
      const normalized = url.startsWith('http') ? url : `https://${url}`;
      const u = new URL(normalized);
      return u.hostname.replace(/^www\./, '');
    } catch {
      return null;
    }
  }

  /** Return a lightweight favicon URL for a given link using Google's favicon service */
  getFaviconUrl(url?: string | null): string {
    const domain = this.getDomain(url) || 'example.com';
    return `https://www.google.com/s2/favicons?sz=64&domain=${encodeURIComponent(domain)}`;
  }

  /** trackBy for ngFor */
  trackByMethodId(_index: number, method: PaymentMethod): string {
    return method.id ?? _index.toString();
  }
}